
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Provider, Validate } from '@/types';
import { buildMemberInfoFromProvider, getMembershipInputSettings } from '@/utils/membership-helpers';
import { MEMBERSHIP_PROVIDERS } from '@/utils/constants';
import { formatPhoneNumber } from '@/utils/format';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import TextField from '@/components/shared/TextField.vue';

@Component<MembershipModal>({
	components: {
		TextField,
		StandardModal,
		TextButton,
		ValidationObserver,
		ValidationProvider,
		WarningModal
	}
})
export default class MembershipModal extends Vue {
	@Action('lookupMembership', { namespace: 'orders' }) private lookupMembership!: ({ identifier } : { identifier: string }) => Promise<MembershipLookupResult|undefined>;
	@Action('applyPromoCode', { namespace: 'orders' }) private applyPromoCode!: ({ memberInfo, menus } : { memberInfo: CheckoutMemberInfo; menus: Menu[]; }) => Promise<void>;
	@Getter('getPublishedMenus', { namespace: 'menus' }) publishedMenus!: Menu[];
	@Getter('getMembershipCustomization', { namespace: 'auth' }) private membershipCustomization!: MembershipCustomization;
	@Prop({ type: Object, required: true }) private checkout!: Checkout;

	$refs!: {
		observer: Validate;
		provider: Provider;
	};

	private identifier: string = '';
	private loading: boolean = false;

	/**
	 * Get the input settings based on the input type
	 *
	 * @return {MembershipInputSettings}
	 */
	private get inputSettings(): MembershipInputSettings {
		return getMembershipInputSettings(this.membershipCustomization);
	}

	/**
	 * Format the phone number - remove non-digital characters
	 * and add dashes to the respective positions
	 *
	 * @return {void}
	 */
	private formatInput(): void {
		if(this.inputSettings.type === 'tel' && this.identifier) {
			this.identifier = formatPhoneNumber(this.identifier);
		}
	}

	/**
	 * Submit the membership identifier and apply the benefits.
	 * Error out without applying the promo code if the membership provided doesn't allow access to the member-only menus.
	 *
	 * @return {Promise<void>}
	 */
	private async submit(): Promise<void> {
		let isValid = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}

		this.loading = true;
		try {
			const tempIdentifier = this.inputSettings.type === 'tel' ? this.identifier.replace(/[^0-9]/g, '') : this.identifier;
			const result: MembershipLookupResult | undefined = await this.lookupMembership({ identifier: tempIdentifier });
			const group = result?.providers.find(providerGroup => providerGroup.provider !== MEMBERSHIP_PROVIDERS.SPH);

			this.loading = false;
			if (result?.member?.id && group) {
				await this.applyPromoCode({ memberInfo: buildMemberInfoFromProvider(result, group), menus: this.publishedMenus });
				this.$emit('success');
			}
			else {
				this.$refs.provider.setErrors([this.$t('orders.membership_modal.non_menu_membership_error')]);
			}
		}
		catch (error) {
			this.loading = false;
			this.$refs.provider.setErrors([this.$t('orders.guest_details.membership.membership_not_applied', { type: this.inputSettings.label.toLowerCase() })]);
		}
	}
}
